import Dialog from "@components/Dialog";
import SubscriptionForm from "@components/SubscriptionForm";
import localizeCurrency from "@helpers/localizeCurrency";
import { useTranslation } from "react-i18next";
import { StripePrice } from "src/types";

interface Props {
  currency: string;
  isSubmitting: boolean;
  open: boolean;
  onClose: () => void;
  periodAmount: number;
  periodUnit: string;
  setIsSubmitting: (isSubmitting: boolean) => void;
  stripeIntroPrice: StripePrice | undefined;
  stripePrice: StripePrice | undefined;
}

export default function LimitedTimeOfferDialog({
  currency,
  isSubmitting,
  onClose,
  open,
  periodAmount,
  periodUnit,
  setIsSubmitting,
  stripeIntroPrice,
  stripePrice,
}: Props) {
  const { t } = useTranslation();

  if (!stripeIntroPrice || !stripePrice) return null;

  const hasCurrencyOption =
    !!stripePrice.currency_options?.[currency]?.unit_amount_decimal;

  const price = parseFloat(
    hasCurrencyOption
      ? stripePrice.currency_options[currency].unit_amount_decimal
      : stripePrice?.unit_amount_decimal,
  );

  const priceCurrency = hasCurrencyOption ? currency : stripePrice.currency;
  const divider = periodAmount === 12 ? 52 : 1;

  if (!price) return null;

  const introPrice = parseFloat(
    hasCurrencyOption
      ? stripeIntroPrice.currency_options[currency].unit_amount_decimal
      : stripeIntroPrice?.unit_amount_decimal,
  );
  const localizePrice = (price: number) =>
    localizeCurrency({ amountInCents: price, currency: priceCurrency });

  return (
    <Dialog onClose={onClose} open={open}>
      <div className="gap-4 text-center">
        <p className="mb-2 text-2xl font-bold">
          {t("pages.checkout.plans.dialog.limitedTimeOffer")}
        </p>

        <p className="text-4xl font-semibold">{periodAmount}</p>

        <p className="mb-3 text-lg uppercase">
          {t(
            `pages.checkout.plans.${periodUnit}_${periodAmount === 1 ? "one" : "other"}`,
          )}
        </p>

        <p className="text-2xl text-custom-gray line-through">
          {localizePrice(price / divider)}
        </p>

        <div className="mb-4 flex flex-col items-center gap-0">
          <p className="text-[40px] font-bold">
            {localizePrice(introPrice / divider)}
          </p>
          <p className="text-white">{t("pages.checkout.plans.perWeek")}</p>
        </div>

        <SubscriptionForm
          buttonTitle={t("pages.checkout.plans.dialog.startNow")}
          currency={priceCurrency}
          isSubmitting={isSubmitting}
          plan="weekly"
          priceId={stripeIntroPrice.id}
          setIsSubmitting={setIsSubmitting}
          price={stripePrice}
        />

        <p className="pt-4 font-semibold text-white">
          {t("pages.checkout.plans.cancelAnytime")}
        </p>

        <p>{t("pages.checkout.plans.moneyBackGuarantee")}</p>
      </div>
    </Dialog>
  );
}
